import { TriangleAlert } from 'lucide-react';

import { formatarData } from '@/lib/formato';
import { Checkbox } from '@/ui/primitives/checkbox';
import { type CartaoCnpj, type RegimeVigencia } from '@fiscoai/contracts';

import { REGIME } from './rotulos';

function vigencia(r: RegimeVigencia): string {
  return `${REGIME[r.regime]} · ${formatarData(r.inicio)} → ${r.fim ? formatarData(r.fim) : 'atual'}`;
}

function enderecoEmLinha(cartao: CartaoCnpj): string {
  const e = cartao.endereco;
  if (!e) return '—';
  return `${e.logradouro}, ${e.numero} · ${e.bairro} · ${e.municipio}/${e.uf}`;
}

/** O que a Receita devolveu, para conferir antes de cadastrar. */
export function ResumoCartao({
  cartao,
  usarRegime,
  aoMudarUsarRegime,
}: {
  cartao: CartaoCnpj;
  usarRegime: boolean;
  aoMudarUsarRegime: (usar: boolean) => void;
}) {
  const principal = cartao.cnaes.find((c) => c.principal);
  return (
    <section
      aria-label="Dados da Receita"
      className="grid animate-surgir gap-3 rounded-cartao border border-borda bg-superficie-afundada p-4 text-sm"
    >
      {!cartao.ativa && (
        <p
          role="alert"
          className="flex items-center gap-2 rounded-controle bg-alerta-suave px-3 py-2 text-alerta"
        >
          <TriangleAlert className="size-4 shrink-0" aria-hidden />
          Situação na Receita: {cartao.situacaoCadastral}
          {cartao.situacaoDesde ? ` desde ${formatarData(cartao.situacaoDesde)}` : ''}
        </p>
      )}
      <dl className="grid gap-x-4 gap-y-2 sm:grid-cols-[auto_1fr]">
        <dt className="text-texto-suave">Nome fantasia</dt>
        <dd>{cartao.nomeFantasia ?? '—'}</dd>
        <dt className="text-texto-suave">Situação</dt>
        <dd>
          {cartao.situacaoCadastral} · {cartao.matriz ? 'Matriz' : 'Filial'}
        </dd>
        <dt className="text-texto-suave">Endereço</dt>
        <dd>{enderecoEmLinha(cartao)}</dd>
        <dt className="text-texto-suave">CNAE principal</dt>
        <dd>{principal ? `${principal.codigo} · ${principal.descricao ?? ''}` : '—'}</dd>
      </dl>
      {cartao.regimesSugeridos.length > 0 && (
        <div className="grid gap-2 border-t border-borda pt-3">
          <p className="text-texto-suave">
            Regime sugerido
            {cartao.ultimoAnoDeclarado
              ? ` (declarado até ${String(cartao.ultimoAnoDeclarado)})`
              : ''}
            :
          </p>
          <ul className="grid gap-1">
            {cartao.regimesSugeridos.map((r) => (
              <li key={`${r.regime}-${r.inicio}`} className="font-medium">
                {vigencia(r)}
              </li>
            ))}
          </ul>
          <Checkbox
            rotulo="Usar o regime sugerido"
            descricao="Confira com o fiscal: a declaração é de anos passados."
            marcado={usarRegime}
            aoMudar={aoMudarUsarRegime}
          />
        </div>
      )}
    </section>
  );
}
